import { useState, useMemo, useEffect } from "react";
import "./day-plan-styles/DayPlanParent.css";
import StatusColumn from "./day-plan-components/StatusColumn";
import Modal from "../modal-component/Modal";
import TaskCompletionPrompt from "./day-plan-components/day-plan-modals/TaskCompletionPrompt";
import WarningPrompt from "./day-plan-components/day-plan-modals/WarningPrompt";
import ProductionBreakdown from "./day-plan-components/day-plan-modals/ProductionBreakdown";
import { useDataContext } from "../../data-context/DataContextProvider";
import { useTranslation } from "@preppa/translation";
import { generateTimeSegments } from "../../utils/timeSegmentation";
import { CustomTaskCard, PlanData, TASK_STATUS } from "../../types/types";
import {
  getStatusFromCode,
  canTransition,
  getTransitionModalType,
  TransitionModalType,
  getStatusCode,
  getDisplayConversion,
  hasProducedInput,
  hasAvailableInput,
} from "../../utils/planDataHelpers";
import { getPlanDayForWorksheet } from "../../utils/dateUtils";
import { buildTaskRange } from "../../utils/taskRange";
import { updatePlanData } from "../../api/apiCallsPlanData";

type PendingTransition = {
  customCard: CustomTaskCard;
  from: string;
  to: string;
};

function CustomPlan() {
  const {
    selectedDayView,
    planData,
    selectedWorksheet,
    selectedDate,
    buCode,
    buType,
    dispatch,
  } = useDataContext();
  const { translate } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<TransitionModalType>("none");
  const [pendingTransition, setPendingTransition] =
    useState<PendingTransition | null>(null);

  const [expandedTodoIndex, setExpandedTodoIndex] = useState<number | null>(
    null,
  );
  const [expandedInProgressIndex, setExpandedInProgressIndex] = useState<
    number | null
  >(null);
  const [expandedDoneIndex, setExpandedDoneIndex] = useState<number | null>(
    null,
  );

  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [breakdownPlanData, setBreakdownPlanData] = useState<PlanData | null>(null);

  const [customCardStatuses, setCustomCardStatuses] = useState<
    Map<string, string>
  >(new Map());

  const timeSegments = useMemo(() => {
    return generateTimeSegments(selectedDayView);
  }, [selectedDayView]);

  const slotBoundaries = useMemo(() => {
    if (!selectedDayView?.splitTimes) return [];
    return selectedDayView.splitTimes
      .map((st) => parseInt(st.splitTime.split(":")[0], 10))
      .filter((h) => !isNaN(h))
      .sort((a, b) => a - b);
  }, [selectedDayView]);

  const allCustomCards = useMemo(() => {
    if (!selectedWorksheet || slotBoundaries.length === 0) return [];

    const shouldIncludeByPlanRules = (
      plannedVol: number | null,
      forecastVol: number | null,
    ): boolean => {
      if (plannedVol === null || plannedVol === undefined) {
        return (forecastVol ?? 0) > 0;
      }
      return plannedVol > 0;
    };

    const getSlotIdx = (hour: number): number => {
      for (let i = 0; i < slotBoundaries.length; i++) {
        if (hour < slotBoundaries[i]) return i;
      }
      return slotBoundaries.length;
    };

    const selectedPlanDay = getPlanDayForWorksheet(
      selectedDate,
      selectedWorksheet.prepDayBefore,
    );
    const dayPlanData = planData.find((day) => day.planDay === selectedPlanDay);
    if (!dayPlanData) return [];

    const cards: CustomTaskCard[] = [];

    dayPlanData.dayPlanDatas.forEach((item) => {
      if (
        !item.worksheetData ||
        item.worksheetData.id !== selectedWorksheet.id
      )
        return;
      if (item.itemSalesPlanDtos.length === 0) return;

      const numSlots = slotBoundaries.length + 1;
      const slotDtos: (typeof item.itemSalesPlanDtos)[] = Array.from(
        { length: numSlots },
        () => [],
      );

      item.itemSalesPlanDtos.forEach((dto) => {
        const hour = parseInt(dto.plannedDayHour.substring(8, 10), 10);
        if (!isNaN(hour)) {
          const idx = getSlotIdx(hour);
          slotDtos[idx].push(dto);
        }
      });

      for (let i = 0; i < numSlots; i++) {
        const dtos = slotDtos[i];
        if (dtos.length === 0) continue;

        const totalPlannedVol = dtos.reduce(
          (sum, dto) => sum + (dto.plannedVol || 0),
          0,
        );
        const totalForecastedVol = dtos.reduce(
          (sum, dto) => sum + (dto.forecastVol || 0),
          0,
        );

        const shouldIncludeSlot = dtos.some((dto) =>
          shouldIncludeByPlanRules(dto.plannedVol, dto.forecastVol),
        );
        if (!shouldIncludeSlot) continue;

        // One task per shift, anchored on the slot's last plan hour so neighbouring
        // shifts never share a toHour.
        const range = buildTaskRange(dtos);
        if (!range) continue;

        cards.push({
          slotIndex: i,
          itemSalesPlan: range.anchorDto,
          parentPlanData: item,
          totalPlannedVol: Math.round(totalPlannedVol * 10) / 10,
          totalForecastedVol: Math.round(totalForecastedVol * 10) / 10,
          fromHour: range.fromHour,
          toHour: range.toHour,
        });
      }
    });

    return cards;
  }, [planData, selectedWorksheet, selectedDate, slotBoundaries]);

  useEffect(() => {
    if (!selectedWorksheet) {
      setCustomCardStatuses(new Map());
      return;
    }

    const initialStatuses = new Map<string, string>();
    allCustomCards.forEach((card) => {
      const key = `${card.parentPlanData.itemCode}-slot-${card.slotIndex}`;
      const status = getStatusFromCode(card.itemSalesPlan.statusCode || "10");
      initialStatuses.set(key, status);
    });
    setCustomCardStatuses(initialStatuses);
  }, [allCustomCards, selectedWorksheet]);

  const getCustomCardKey = (card: CustomTaskCard): string => {
    return `${card.parentPlanData.itemCode}-slot-${card.slotIndex}`;
  };

  const customTodoCards = useMemo(() => {
    return allCustomCards.filter((card) => {
      return (
        customCardStatuses.get(getCustomCardKey(card)) === TASK_STATUS.TODO
      );
    });
  }, [allCustomCards, customCardStatuses]);

  const customInProgressCards = useMemo(() => {
    return allCustomCards.filter((card) => {
      return (
        customCardStatuses.get(getCustomCardKey(card)) ===
        TASK_STATUS.IN_PROGRESS
      );
    });
  }, [allCustomCards, customCardStatuses]);

  const customDoneCards = useMemo(() => {
    return allCustomCards.filter((card) => {
      return (
        customCardStatuses.get(getCustomCardKey(card)) === TASK_STATUS.DONE
      );
    });
  }, [allCustomCards, customCardStatuses]);

  const customTodoBuckets = useMemo(() => {
    const buckets = new Map<string, CustomTaskCard[]>();
    const numSlots = slotBoundaries.length + 1;
    for (let i = 0; i < numSlots; i++) {
      buckets.set(`slot-${i}`, []);
    }
    customTodoCards.forEach((card) => {
      const bucket = buckets.get(`slot-${card.slotIndex}`);
      if (bucket) bucket.push(card);
    });
    return buckets;
  }, [customTodoCards, slotBoundaries]);

  const customInProgressBuckets = useMemo(() => {
    const buckets = new Map<string, CustomTaskCard[]>();
    buckets.set("all", customInProgressCards);
    return buckets;
  }, [customInProgressCards]);

  const customDoneBuckets = useMemo(() => {
    const buckets = new Map<string, CustomTaskCard[]>();
    buckets.set("all", customDoneCards);
    return buckets;
  }, [customDoneCards]);

  function updateCustomTaskStatus(
    card: CustomTaskCard,
    from: string,
    to: string,
  ): string {
    const key = getCustomCardKey(card);
    const previousStatus = customCardStatuses.get(key) || from;

    setCustomCardStatuses((prev) => {
      const newStatuses = new Map(prev);
      newStatuses.set(key, to);
      return newStatuses;
    });

    return previousStatus;
  }

  function revertCustomTaskStatus(
    card: CustomTaskCard,
    previousStatus: string,
  ): void {
    const key = getCustomCardKey(card);

    setCustomCardStatuses((prev) => {
      const newStatuses = new Map(prev);
      newStatuses.set(key, previousStatus);
      return newStatuses;
    });
  }

  const callCustomStatusUpdateAPI = async (
    card: CustomTaskCard,
    newStatus: string,
    producedVol?: number,
    availableVol?: number,
  ): Promise<boolean> => {
    const statusCode = getStatusCode(newStatus);

    const finalProducedVol =
      producedVol !== undefined
        ? producedVol
        : card.itemSalesPlan.producedVol || 0;
    const finalAvailableVol =
      availableVol !== undefined
        ? availableVol
        : card.itemSalesPlan.availableVol || 0;
    const comment = card.itemSalesPlan.comment || "";

    return await updatePlanData({
      itemCode: card.parentPlanData.itemCode,
      fromHour: card.fromHour,
      toHour: card.toHour,
      buCode,
      buType,
      statusCode,
      producedVol: finalProducedVol,
      availableVol: finalAvailableVol,
      comment,
      callBack: dispatch,
    });
  };

  async function handleCustomStatusTransition(
    card: CustomTaskCard,
    from: string,
    to: string,
  ) {
    if (!canTransition(from, to)) {
      console.warn(`Transition from ${from} to ${to} is not allowed`);
      return;
    }

    const producedVol = hasProducedInput(card.parentPlanData) ? (card.itemSalesPlan.producedVol || 0) : 0;
    const availableVol = hasAvailableInput(card.parentPlanData) ? (card.itemSalesPlan.availableVol || 0) : 0;
    const plannedVol = card.totalPlannedVol || card.totalForecastedVol;

    const modalTypeNeeded = getTransitionModalType(
      from,
      to,
      producedVol,
      availableVol,
      plannedVol,
    );

    if (modalTypeNeeded === "blocked") return;

    const effectiveModalType = (modalTypeNeeded === "produceQuantity" && !hasProducedInput(card.parentPlanData))
      ? "none"
      : modalTypeNeeded;

    if (effectiveModalType === "none") {
      const previousStatus = updateCustomTaskStatus(card, from, to);
      const success = await callCustomStatusUpdateAPI(card, to);
      if (!success) {
        revertCustomTaskStatus(card, previousStatus);
      }
    } else {
      setPendingTransition({ customCard: card, from, to });
      setModalType(effectiveModalType);
      setIsModalOpen(true);
    }
  }

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setPendingTransition(null);
    setModalType("none");
  };

  const handleProduceQtyConfirm = async (producedQty: number) => {
    if (!pendingTransition) return;

    const { customCard, from, to } = pendingTransition;

    handleModalCancel();

    const previousStatus = updateCustomTaskStatus(customCard, from, to);
    const success = await callCustomStatusUpdateAPI(
      customCard,
      to,
      producedQty,
    );
    if (!success) {
      revertCustomTaskStatus(customCard, previousStatus);
    }
  };

  const handleWarningConfirm = async () => {
    if (!pendingTransition) return;

    const { customCard, from, to } = pendingTransition;

    handleModalCancel();

    const previousStatus = updateCustomTaskStatus(customCard, from, to);
    const success = await callCustomStatusUpdateAPI(
      customCard,
      to,
      0,
      hasAvailableInput(customCard.parentPlanData) ? (customCard.itemSalesPlan.availableVol || 0) : 0,
    );
    if (!success) {
      revertCustomTaskStatus(customCard, previousStatus);
    }
  };

  const handleExpansionToggleTodo = (index: number) => {
    setExpandedTodoIndex(expandedTodoIndex === index ? null : index);
  };

  const handleExpansionToggleInProgress = (index: number) => {
    setExpandedInProgressIndex(
      expandedInProgressIndex === index ? null : index,
    );
  };

  const handleExpansionToggleDone = (index: number) => {
    setExpandedDoneIndex(expandedDoneIndex === index ? null : index);
  };

  const handleViewBreakdown = (planData: PlanData) => {
    setBreakdownPlanData(planData);
    setIsBreakdownOpen(true);
  };

  const handleBreakdownClose = () => {
    setIsBreakdownOpen(false);
    setBreakdownPlanData(null);
  };

  return (
    <div id="day-plan-container">
      <div className="task-status-wrapper">
        <StatusColumn
          status={TASK_STATUS.TODO}
          label={translate("dayPlan.status.todo")}
          customBuckets={customTodoBuckets}
          timeSegments={timeSegments}
          expandedIndex={expandedTodoIndex}
          onToggleExpand={handleExpansionToggleTodo}
          onCustomStatusChange={handleCustomStatusTransition}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.IN_PROGRESS}
          label={translate("dayPlan.status.inProgress")}
          customBuckets={customInProgressBuckets}
          timeSegments={[]}
          expandedIndex={expandedInProgressIndex}
          onToggleExpand={handleExpansionToggleInProgress}
          onCustomStatusChange={handleCustomStatusTransition}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.DONE}
          label={translate("dayPlan.status.done")}
          customBuckets={customDoneBuckets}
          timeSegments={[]}
          expandedIndex={expandedDoneIndex}
          onToggleExpand={handleExpansionToggleDone}
          onCustomStatusChange={handleCustomStatusTransition}
          onViewBreakdown={handleViewBreakdown}
        />
      </div>
      <Modal isModalOpen={isModalOpen} onClose={handleModalCancel}>
        {modalType === "produceQuantity" && pendingTransition && (() => {
          const pd = pendingTransition.customCard.parentPlanData;
          const dc = getDisplayConversion(pd);
          const cf = dc?.conversionFactor ?? 1;
          const uom = dc ? dc.displayUom : (
            pendingTransition.customCard.itemSalesPlan.uomCode || ""
          );
          return (
          <TaskCompletionPrompt
            initialProducedQty={
              pendingTransition.customCard.itemSalesPlan.producedVol || 0
            }
            uomCode={uom}
            conversionFactor={cf}
            originStatus={pendingTransition.from}
            totalPlannedVol={
              pendingTransition.customCard.totalPlannedVol > 0
                ? pendingTransition.customCard.totalPlannedVol
                : pendingTransition.customCard.totalForecastedVol
            }
            availableVol={
              hasAvailableInput(pendingTransition.customCard.parentPlanData) ? (pendingTransition.customCard.itemSalesPlan.availableVol || 0) : 0
            }
            onConfirm={handleProduceQtyConfirm}
            onCancel={handleModalCancel}
          />
          );
        })()}
        {modalType === "warning" && pendingTransition && (
          <WarningPrompt
            message={translate("dayPlan.prompts.warningMoveBackToTodo")}
            onConfirm={handleWarningConfirm}
            onCancel={handleModalCancel}
          />
        )}
      </Modal>
      <Modal isModalOpen={isBreakdownOpen} onClose={handleBreakdownClose}>
        {breakdownPlanData && (
          <ProductionBreakdown
            planData={breakdownPlanData}
            onClose={handleBreakdownClose}
          />
        )}
      </Modal>
    </div>
  );
}

export default CustomPlan;